import React from 'react'
import {motion} from 'framer-motion'
import {useSiteContent} from '../../hooks/useSiteContent'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiTarget, FiUsers, FiTrendingUp, FiArrowRight} = FiIcons

const iconMap = {
  target: FiTarget,
  users: FiUsers,
  trendingUp: FiTrendingUp
}

const HowItWorksSection = ({content = {}, onCTAClick}) => {
  const {content: siteContent} = useSiteContent()
  
  const {
    title = "How It Works",
    subtitle = "Our proven 3-step approach to lasting transformation",
    steps = []
  } = content

  const defaultSteps = [
    {
      title: "PPC Method",
      description: siteContent?.howitworks_ppc_definition || "PPC stands for Protein, Produce, and Carbs - the foundations of sustainable nutrition",
      icon: "target"
    },
    {
      title: "Real-World Coaching", 
      description: "Practical strategies that fit your busy lifestyle and real challenges",
      icon: "users"
    },
    {
      title: "Data-Informed Progress",
      description: "Track what matters and make adjustments based on your results", 
      icon: "trendingUp"
    }
  ]

  const stepsToShow = steps.length > 0 ? steps : defaultSteps

  const handleViewCourses = () => {
    onCTAClick?.('view_courses_how_it_works', '/courses')
  }

  const handleGetConsultation = () => {
    onCTAClick?.('get_consultation_how_it_works', siteContent?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation')
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {stepsToShow.map((step, index) => {
            const IconComponent = iconMap[step.icon] || FiTarget
            return (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: index * 0.2}}
                viewport={{once: true}}
                className="relative group"
              >
                {/* Step Number */}
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-nutrition-600 text-white rounded-full flex items-center justify-center font-bold text-lg z-10">
                  {index + 1}
                </div>

                {/* Card */}
                <div className="bg-gray-50 hover:bg-white border border-gray-100 hover:border-nutrition-200 rounded-2xl p-8 transition-all duration-300 hover:shadow-xl group-hover:transform group-hover:-translate-y-2 h-full">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-nutrition-100 group-hover:bg-nutrition-600 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
                    <SafeIcon 
                      icon={IconComponent} 
                      className="w-8 h-8 text-nutrition-600 group-hover:text-white transition-colors duration-300" 
                    />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Arrow connector (hidden on last item) */}
                  {index < stepsToShow.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-20">
                      <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-nutrition-400" />
                    </div>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.6}}
          viewport={{once: true}}
          className="text-center mt-16"
        >
          <div className="bg-nutrition-50 rounded-2xl p-8 border border-nutrition-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Transformation?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Join thousands who've discovered the sustainable approach to lasting weight loss. 
              No more yo-yo dieting, just real results that stick.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/courses"
                onClick={handleViewCourses}
                className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
              >
                <span>View Our Courses</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </a>
              <a
                href={siteContent?.cta_secondary_url || 'https://tidycal.com/empowernutrition/30-minute-consultation'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleGetConsultation}
                className="border border-nutrition-600 text-nutrition-600 hover:bg-nutrition-600 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                {siteContent?.transformation_cta_label || 'Book a Consultation'}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default HowItWorksSection